import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Close, Favorite, FavoriteBorder } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  styled,
  Tooltip,
  CircularProgress,
  Stack,
  Button,
  alpha,
  lighten,
} from "@mui/material";
import { connect, useDispatch, useSelector } from "react-redux";
import { addToFavorite, removeFavorite } from "../actions";
import { getOpenHouseTime, includesAnyWord } from "../utils/functions";
import _ from "lodash";
import { FavoritePopUp } from "./favorites/Favorite";
import {
  BedIcon,
  SquareFootIcon,
  ShowerIcon,
  MarkerIcon,
  ShareIcon,
} from "./Icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

import ShareProperty from "./PropertyDetails/ShareProperty";
import MLSLogo from "../assets/mlsLogo.png";

const StyledCard = styled(Card)(({ theme, border }) => ({
  width: "100%",
  boxShadow: "0 1px 10px rgba(0,0,0, 12%)",
  borderRadius: "16px",
  border: border ? `1px solid ${theme.secondaryColor}` : `1px solid #fff`,
  transition: "all .2s ease",
  "&:hover": {
    border: `1px solid ${theme.secondaryColor}`,
    cursor: "pointer",
    "& #lb": {
      background: theme.palette.primary.main,
      color: "#000",
      fontWeight: 500,
    },
  },
}));
const ImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.background.avatar,
  overflow: "hidden",
  "& .swiper-pagination-bullet": {
    backgroundColor: lighten(theme.palette.paper.main, 0.3),
  },
  "& .swiper-pagination-bullet-active": {
    backgroundColor: theme.palette.paper.main,
  },
}));
const ImageBox = styled(Box)(({ theme, image }) => ({
  position: "absolute",
  left: 0,
  top: 0,
  height: "100%",
  width: "100%",
  zIndex: 5,
  backgroundImage: `url(${image})`,
  backgroundPosition: "center",
  backgroundSize: "cover",
  transition: theme.transitions.create(["transform", "display"], {
    duration: "2s",
  }),
  "&:hover": {
    "-webkit-transform": "scale(105%)",
    transform: "scale(110%)",
  },
}));
const ChipsBox = styled(Box)(() => ({
  position: "absolute",
  left: 0,
  top: 0,
  width: "calc(100% - 1.5rem)",
  zIndex: 10,
  padding: "1rem 0.75rem 1rem 0.75rem",
}));
const StyledChip = styled(Chip)(() => ({
  boxShadow: "0 1px 5px rgba(0,0,0, 20%)",
  transition: "all .2s ease",
  height: "1.5rem",
  fontSize: "12px",
  "& .MuiChip-label": {
    padding: "0 12px",
  },
}));
const StyledIconButton = styled(IconButton)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  right: "1rem",
  top: "1rem",
  backgroundColor: alpha("#000", 0.8),
  zIndex: 1000,
  width: "2rem",
  height: "2rem",
}));
const StyledButton = styled(Button)(({ theme }) => ({
  overflow: "hidden",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "14px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  borderRadius: 999,
  height: "40px",
  color: theme.palette.tertiary.main,
  borderColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: lighten(theme.palette.primary.main, 0.8),
  },
}));

const ListingCard = (props) => {
  const {
    id,
    inView,
    price,
    image,
    bed,
    bath,
    area,
    address,
    mlsId,
    border,
    addToFavorite,
    removeFavorite,
    listDate,
    vendor,
    status,
    startTime,
    endTime,
    active,
    office,
    close = false,
    onHandleClose,
    virtualTourUrl = "",
    // videoLink = "",
    photos = [],
    agentListed = false,
  } = props;

  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.currentUser);
  const dispatch = useDispatch();

  const [share, setShare] = useState(false);
  const [load, setLoad] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const swiperInstanceRef = useRef(null);

  const propertyUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }
    const baseUrl = window.location.origin;
    let path;
    switch (status) {
      case "openhouse":
        path = `/open-houses/${mlsId}/details/${vendor}`;
        break;
      case "soldhomes":
        path = `/sold-homes/${mlsId}/details/${vendor}`;
        break;
      case "rental":
        path = `/rental/${mlsId}/details/${vendor}`;
        break;
      default:
        path = `/listing/${mlsId}/details/${vendor}`;
        break;
    }
    return baseUrl + path;
  }, [status, mlsId, vendor]);

  const has3DTour = useMemo(() => {
    if (
      virtualTourUrl &&
      includesAnyWord(virtualTourUrl, ["matterport", "immoviewer"])
    )
      return true;
    return false;
  }, [virtualTourUrl]);

  const hasVideo = useMemo(() => {
    // if (
    //   (virtualTourUrl || videoLink) &&
    //   (includesAnyWord(virtualTourUrl, ["video", "youtube", "youtu.be"]) ||
    //     includesAnyWord(videoLink, ["video", "youtube", "youtu.be"]))
    // )
    if (
      virtualTourUrl &&
      includesAnyWord(virtualTourUrl, ["video", "youtube", "youtu.be"]) // media, "vimeo"
    )
      return true;
    return false;
  }, [virtualTourUrl]);

  const shareListing = async (e) => {
    e.stopPropagation();
    if (currentUser) {
      setShare(true);
    } else {
      dispatch({ type: "AUTH_LOGIN" });
    }
  };

  const addToFav = async (e) => {
    e.stopPropagation();
    if (currentUser) {
      if (currentUser.favs.filter((el) => el.property_id === mlsId)[0]) {
        //remove from favs
        setLoad(true);
        const status = await removeFavorite(mlsId);
        if (status === 204) {
          setLoad(false);

          dispatch({
            type: "SUCCESS",
            payload: "Property removed from favorites",
          });
        } else {
          setLoad(false);
          dispatch({
            type: "SUCCESS",
            payload: "Sorry, could not add to favorite",
          });
        }
      } else {
        //add to favs
        setLoad(true);

        const data = {
          user_id: currentUser.id,
          property_id: mlsId,
          address: address,
          price: Number(price.split(",").join("")),
          image: image,
          bedrooms: bed,
          baths: bath,
          area: area,
          list_date: new Date(listDate),
        };

        const status = await addToFavorite(data);
        dispatch({
          type: "FAVORITE",
          payload: data,
        });
        if (status === 200) {
          setLoad(false);
          setOpen(true);
          // dispatch({ type: "SUCCESS", payload: "Property added to favorites" });
        } else {
          setLoad(false);
          dispatch({
            type: "ERROR",
            payload: "Sorry, could not add to favorite",
          });
        }
      }
    } else {
      dispatch({ type: "AUTH_LOGIN" });
    }
  };

  const getDaysOnMarket = (listDate) => {
    if (listDate) {
      const diff = new Date().getTime() - new Date(listDate).getTime();
      const dy = Math.round(diff / (1000 * 3600 * 24)); // Diff in days
      const hr = Math.round(diff / (1000 * 60 * 60)); // Diff in hours

      if (dy <= 30) {
        return hr <= 48 ? `New ${hr}hrs Ago` : "New on BPO Homes";
      } else {
        return "";
      }
    }
    return "";
  };

  const handleSwiperInit = (swiper) => {
    swiperInstanceRef.current = swiper;
    if (swiper.autoplay.running) swiper.autoplay.stop();
  };

  const handleClick = () => {
    if (agentListed) {
      window.open(
        `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}view-listing/${
          props?.agent_id || props?.agentId || id
        }/${id}#home`,
        "_blank"
      );
    } else {
      // localStorage.setItem("priorRoute", window.location.pathname);
      status === "openhouse"
        ? navigate(`/open-houses/${mlsId}/details/${vendor}`)
        : status === "soldhomes"
        ? navigate(`/sold-homes/${mlsId}/details/${vendor}`)
        : status === "rental"
        ? navigate(`/rental/${mlsId}/details/${vendor}`)
        : navigate(`/listing/${mlsId}/details/${vendor}`);
    }
  };

  const showLeftChip = useMemo(() => {
    return (
      (getDaysOnMarket(listDate) &&
        getDaysOnMarket(listDate).includes("New") &&
        !["Sold", "Closed"].includes(active)) ||
      has3DTour ||
      hasVideo
    );
  }, [listDate, active, has3DTour, hasVideo]);

  useEffect(() => {
    const currentCardRef = cardRef.current;
    const swiper = swiperInstanceRef.current;

    const handleMouseEnter = () => {
      if (swiper) swiper?.autoplay?.start();
    };

    const handleMouseLeave = () => {
      if (swiper) swiper?.autoplay?.stop();
    };

    if (currentCardRef) {
      currentCardRef.addEventListener("mouseenter", handleMouseEnter);
      currentCardRef.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (currentCardRef) {
        currentCardRef.removeEventListener("mouseenter", handleMouseEnter);
        currentCardRef.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      const res =
        currentUser?.favs.filter((el) => {
          return el.property_id === mlsId;
        }).length > 0;
      setIsFav(res);
    } else {
      setIsFav(false);
    }
  }, [currentUser, mlsId]);

  return (
    <Box position="relative">
      <StyledCard
        id={id}
        ref={cardRef}
        border={border}
        style={{
          border: inView ? "4px solid" : "none",
          borderColor: inView ? "secondary.main" : "transparent",
        }}
        onClick={handleClick}
      >
        {close && (
          <StyledIconButton
            className="close-icon"
            onClick={(e) => {
              e.stopPropagation();
              onHandleClose();
            }}
          >
            <Close fontSize="small" color="paper" />
          </StyledIconButton>
        )}

        <ImageWrapper height="14rem">
          {!close && (
            <ChipsBox>
              {status === "openhouse" ? (
                <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                  <StyledChip
                    component={"span"}
                    sx={{
                      marginLeft: "5px",
                      bgcolor: "primary.main",
                    }}
                    size="small"
                    label={
                      <Typography sx={{ fontSize: ".7rem", fontWeight: 400 }}>
                        {getOpenHouseTime(startTime, endTime)}
                      </Typography>
                    }
                  />
                  {(has3DTour || hasVideo) && (
                    <StyledChip
                      component={"span"}
                      sx={{
                        bgcolor: "secondary.main",
                        fontWeight: "600",
                        color: "paper.main",
                        textTransform: "uppercase",
                        fontSize: "10.5px",
                      }}
                      size="small"
                      label={
                        <>
                          {has3DTour && "3D"}
                          {hasVideo && "Video"} Tour
                        </>
                        // <>
                        //   {has3DTour && "3D"}
                        //   {hasVideo
                        //     ? `${has3DTour ? "& " : ""} Video`
                        //     : ""}{" "}
                        //   Tour
                        // </>
                      }
                    />
                  )}
                </Stack>
              ) : (
                <Stack
                  direction={"row"}
                  sx={{
                    display: showLeftChip ? "flex" : "inline-block",
                    justifyContent: showLeftChip ? "space-between" : "start",
                    alignItems: "center",
                  }}
                >
                  {showLeftChip && (
                    <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                      <StyledChip
                        component={"span"}
                        sx={{
                          bgcolor: getDaysOnMarket(listDate)?.includes(
                            "hrs Ago"
                          )
                            ? "primary.active"
                            : "primary.main",
                          color: getDaysOnMarket(listDate)?.includes("hrs Ago")
                            ? "paper.main"
                            : "text.main",
                        }}
                        size="small"
                        label={getDaysOnMarket(listDate)}
                      />

                      {(has3DTour || hasVideo) && (
                        <StyledChip
                          component={"span"}
                          sx={{
                            bgcolor: "secondary.main",
                            fontWeight: "600",
                            color: "paper.main",
                            textTransform: "uppercase",
                            fontSize: "10.5px",
                          }}
                          size="small"
                          label={
                            <>
                              {has3DTour && "3D"}
                              {hasVideo && "Video"} Tour
                            </>
                          }
                        />
                      )}
                    </Stack>
                  )}

                  {active === "Sold" || active === "Closed" ? (
                    <StyledChip
                      component={"span"}
                      size="small"
                      sx={{
                        color: "#fff",
                        marginLeft: "auto",
                        bgcolor: "secondary.main",
                      }}
                      label={
                        <Typography
                          sx={{ fontSize: "11px", fontWeight: 500 }}
                        >{`${
                          "SOLD " +
                          new Date(listDate)
                            .toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                            ?.toUpperCase()
                        }`}</Typography>
                      }
                    />
                  ) : (
                    <StyledChip
                      component={"span"}
                      size="small"
                      sx={{
                        marginLeft: "auto",
                        bgcolor: "#fff",
                      }}
                      label={
                        <Typography sx={{ fontSize: ".8rem" }}>
                          {active === "PendingDoNotShow"
                            ? "Pending"
                            : active === ""
                            ? "Pending"
                            : _.startCase(active)}
                        </Typography>
                      }
                    />
                  )}
                </Stack>
              )}
            </ChipsBox>
          )}

          {photos?.length > 0 ? (
            <Swiper
              className="listing-card"
              modules={[Autoplay, Pagination]}
              slidesPerView={1}
              loop
              pagination={{
                clickable: false,
              }}
              autoplay={{
                delay: 2000,
                disableOnInteraction: false,
              }}
              onSwiper={handleSwiperInit}
              style={{ height: "100%" }}
            >
              {photos?.slice(0, 5)?.map((item, index) => {
                return (
                  <SwiperSlide style={{ height: "100%" }} key={index}>
                    <ImageBox image={item} />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          ) : (
            <ImageBox image={image} />
          )}

          {/* Display mls logo */}
          {["mls", "crmls"].includes(vendor) && (
            <Box position="absolute" bottom={0} right={0} pr={0.5} zIndex={99}>
              <img width={43} height={25} src={MLSLogo} alt="mls-logo" />
            </Box>
          )}
        </ImageWrapper>

        <CardContent sx={{ padding: "1rem !important" }}>
          <Stack gap={1.5}>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Stack direction={"row"} gap={".5rem"} alignItems={"center"}>
                <Typography
                  component={"p"}
                  variant="h6"
                  sx={{ fontWeight: 600, fontSize: "1.25rem" }}
                >
                  ${price}
                </Typography>
                {(active === "Sold" || active === "Closed") && (
                  <Typography
                    variant="body2"
                    sx={{ fontSize: ".9rem" }}
                    color={"GrayText"}
                  >
                    Sold Price
                  </Typography>
                )}
              </Stack>

              <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                <IconButton disableTouchRipple onClick={shareListing}>
                  <ShareIcon sx={{ fontSize: "1.20rem" }} />
                </IconButton>

                {active === "Sold" || active === "Closed" ? null : (
                  <IconButton disableTouchRipple onClick={addToFav}>
                    {load ? (
                      <CircularProgress size="1.20rem" color="alternate" />
                    ) : isFav ? (
                      <Favorite color="alternate" fontSize="small" />
                    ) : (
                      <FavoriteBorder color="alternate" fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Stack>
            </Box>

            <Stack gap={1.5}>
              <Stack direction="row" justifyContent={"space-between"}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <BedIcon color="secondary" sx={{ fontSize: "1.5rem" }} />
                  <Typography fontWeight={400} color={"black"} fontSize={14}>
                    {bed ? bed : "—"} beds
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <ShowerIcon color="secondary" sx={{ fontSize: "1.18rem" }} />
                  <Typography fontWeight={400} color={"black"} fontSize={14}>
                    {bath ? bath : "—"} showers
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <SquareFootIcon
                    color="secondary"
                    sx={{ fontSize: "1.24rem" }}
                  />
                  <Typography fontWeight={400} color={"black"} fontSize={14}>
                    {area ? Number(area)?.toLocaleString() : "—"} sq ft
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <MarkerIcon
                  color="secondary"
                  sx={{ fontSize: "1.3rem", marginLeft: "1px" }}
                />
                <Typography
                  title={address}
                  component="h3"
                  variant="body4"
                  color="black"
                  fontWeight={400}
                  noWrap
                >
                  {address}
                </Typography>
              </Box>

              {!close && !agentListed && (
                <>
                  {["Sold", "Closed", "Off Market"].includes(active) &&
                    office?.name && (
                      <Typography
                        component={"p"}
                        mt={0.5}
                        sx={{ fontSize: ".8rem" }}
                        noWrap
                        color={"textSecondary"}
                      >
                        Listing by {office?.name}
                      </Typography>
                    )}
                  {["Sold", "Closed", "Off Market"].includes(active) ? null : (
                    <Stack gap={1.5}>
                      <Tooltip
                        sx={{ bgcolor: "white" }}
                        arrow
                        title={
                          <Typography
                            fontWeight={400}
                            variant="body2"
                            fontSize={".8rem"}
                          >
                            Want to <strong>Fix,Flip,Rent or Develop</strong>?
                            Use our <strong>Deal Analyzer</strong> for FREE!{" "}
                          </Typography>
                        }
                      >
                        <StyledButton
                          size="small"
                          variant="outlined"
                          color="primary"
                          disableElevation
                          disableRipple
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.assign(
                              `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}analyze-deal/?address=${address}`
                            );
                          }}
                        >
                          Analyze This Property
                        </StyledButton>
                      </Tooltip>

                      <Tooltip
                        arrow
                        title={
                          <Typography
                            fontWeight={500}
                            variant="body2"
                            fontSize={".7rem"}
                          >
                            {" "}
                            DON’T TRUST AI ESTIMATES. Before you Buy, get a{" "}
                            <strong strong> Broker Price Opinion</strong> from a{" "}
                            <strong> Local Agent TODAY!</strong>
                          </Typography>
                        }
                      >
                        <StyledButton
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.assign(
                              `${process.env.REACT_APP_BPOHOMESTECH_REDIRECT}order-bpo-report`
                            );
                          }}
                          size="small"
                          disableElevation
                          disableRipple
                          variant="outlined"
                          color="primary"
                        >
                          Request Agent Insight
                        </StyledButton>
                      </Tooltip>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </StyledCard>

      {share && (
        <ShareProperty share={share} setShare={setShare} url={propertyUrl} />
      )}
      {open && (
        <FavoritePopUp
          open={open}
          setOpen={setOpen}
          styles={{
            width: "100%",
            bottom: "0% !important",
          }}
        />
      )}
    </Box>
  );
};

const mapStateToprops = (state) => {
  return state;
};

export default connect(mapStateToprops, { addToFavorite, removeFavorite })(
  ListingCard
);
